import digitalData from 'digital-data';
import { setAnalytics } from '../../../thd-analytics-utils';
import { PAGE_WIDTH_MAP, SERVICES_ONLINE_EXPRESS_BASENAMES, B2C, B2B } from '../../../constants';

// Use imported constants
const BASENAMES = SERVICES_ONLINE_EXPRESS_BASENAMES;

/**
 * Get current device type based on screen width
 * @param {number} width - Screen width
 * @returns {string} Device type (desktop, tablet, mobile)
 */
export const getCurrentDevice = (width) => {
  const screen = PAGE_WIDTH_MAP.find(({ size }) => width > size);
  return (screen && screen.name) || 'mobile';
};

/**
 * Transform slug by replacing hyphens and spaces with underscores
 * @param {string} slug - Input slug
 * @returns {string} Transformed slug
 */
export const transformSlug = (slug) => {
  return typeof slug === 'string' ? slug.trim().replace(/-| /g, '_') : '';
};

/**
 * Get transformed page name for analytics
 * @param {string} serviceSlug - Service slug
 * @param {string} controlSlug - Control slug (variant)
 * @param {string} serviceName - Service name from breadcrumb
 * @returns {string} Transformed page name
 */
export const getTransformedPageName = (serviceSlug, controlSlug, serviceName = '') => {
  // For services with a generic slug ('g') the serviceName (breadcrumb) will be used
  if (serviceSlug === 'g' && serviceName) {
    return transformSlug(serviceName.toLowerCase());
  }
  // Use control slug as page name if present (case of a variant)
  return transformSlug(controlSlug || serviceSlug);
};

/**
 * Get LivePerson ID from window object
 * @returns {string} LivePerson ID
 */
export const getLivePersonId = () => {
  return window.lpTag?.eventFunctions?.data?.LPVID || '';
};

/**
 * Get THD Localizer instance
 * @returns {object|null} Localizer instance
 */
export const getLocalizer = () => {
  return window.THD_LOCALIZER_AUTO_INIT ? window.THD_LOCALIZER_AUTO_INIT.Localizer : null;
};

/**
 * Set nested property in object without lodash
 * @param {object} obj - Target object
 * @param {string} path - Property path (dot notation)
 * @param {any} value - Value to set
 */
export const setNestedProperty = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;
  
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }
  
  current[keys[keys.length - 1]] = value;
};

/**
 * Initialize digital data with base properties
 * @param {string} integration - Integration value to set
 * @param {object} thdCustomer - THD Customer object
 */
export const initDigitalData = (integration = '', thdCustomer) => {
  // Set site properties
  setNestedProperty(digitalData, 'site.experienceType', 
    getCurrentDevice(document.documentElement.clientWidth));
  setNestedProperty(digitalData, 'site.siteID', BASENAMES.CATEGORY);
  setNestedProperty(digitalData, 'site.betaSite', 
    document.cookie.includes('HD_DC=beta') ? 'beta' : 'prod');
  setNestedProperty(digitalData, 'site.integration', integration);
  if (thdCustomer) {
    // Add defensive checks with strict boolean comparison
    const isB2C = thdCustomer.isB2CCustomer === true;
    const businessType = isB2C ? B2C : B2B;
    
    setNestedProperty(digitalData, 'site.businessType', businessType);
  } else {
    console.warn('THD Customer not available, setting businessType to null');
    setNestedProperty(digitalData, 'site.businessType', null);
  }

  // Remove existing form data
  delete digitalData.form;

  // Set page structure
  digitalData.page = {
    pageInfo: {
      pageType: BASENAMES.PAGE,
      pageName: '',
      pageLoadEvent: '',
      abTest: '',
    },
    category: {
      primaryCategory: BASENAMES.CATEGORY,
      subCategory1: '',
      subCategory2: '',
    },
    form: {
      formType: BASENAMES.FORMTYPE,
      formName: '',
    },
  };

  // Set user localization data
  const localizer = getLocalizer();
  const deliveryZipCodeInfo = localizer ? localizer.getDeliveryZipcodeDetails() : null;
  setNestedProperty(digitalData, 'user.localization.userZip',
    (deliveryZipCodeInfo && deliveryZipCodeInfo.zipcode) || '');

  const localizedStore = localizer ? localizer.getLocalizedStore() : null;
  
  if (thdCustomer && thdCustomer.isB2CCustomer && localizer) {
    setNestedProperty(digitalData, 'user.localization.inStoreNumber',
      !localizer.status.isUserInStore() ? 'Not In Store' : undefined);
    setNestedProperty(digitalData, 'user.localization.method',
      localizer && localizer.status.getLocalizationMethod());
    setNestedProperty(digitalData, 'user.localization.gpsPermission',
      (localizer && localizer.status.getGpsStatus()) || '');

    setNestedProperty(digitalData, 'user.localization.storeNumber',
      (localizedStore && localizedStore.storeId) || 'not localized');
    setNestedProperty(digitalData, 'user.localization.storeState',
      (localizedStore && localizedStore.state) || '');
    setNestedProperty(digitalData, 'user.localization.storeZip',
      (localizedStore && localizedStore.zipcode) || '');

    setNestedProperty(digitalData, 'user.profile.authenticationStatus',
      (thdCustomer.isLoggedIn && 'authenticated') || 'guest');
    setNestedProperty(digitalData, 'user.profile.profileInfo.profileID',
      thdCustomer.userID);
  }

  // Initialize THD Analytics Event structure
  digitalData.thdAnalyticsEvent = {
    form: {
      livePersonID: '',
    },
  };

  // Initialize product array if needed
  digitalData.product = digitalData.product || [];
};

/**
 * Publish event to digital data
 * @param {object} eventObject - Event object to publish
 */
export const publishEvent = ({ eventObject }) => {
  try {
    digitalData.event = digitalData.event || [];
    digitalData.event.push(eventObject);
    setAnalytics(digitalData);
  } catch (error) {
    console.error('Error publishing event:', error);
  }
};

/**
 * Publish page load analytics
 */
export const publishPageLoadAnalytics = () => {
  try {
    setAnalytics(digitalData);
  } catch (error) {
    console.error('Error publishing page load analytics:', error);
  }
};